import {createReducer, on} from "@ngrx/store";
import {ISharedLoginStoreState} from "./shared-login.state";
import {SharedLoginActions} from "@shared/components/login/store/shared-login.actions-type";
import {EAppType} from "@shared/models/AppType.enum";
import {Client} from "@ionic/cli/lib/http";
import {Brands, Clients, QrTokenDto} from "@server-models";

export const initialLoginState: ISharedLoginStoreState = <ISharedLoginStoreState>{
  canUseApp: false,
  isGuest: false,
  isLoading: false,
  isMultiTenant: false,
  currentApp: undefined,
  currentUser: undefined,
  currentToken: undefined,
  currentTenant: undefined,
  currentLocation: undefined
};

export const sharedLoginStore = createReducer(
  initialLoginState,
  on(SharedLoginActions.setAppType, (state, { appType }) => ({
    ...state,
    currentApp: appType,
    currentAppSegment: getAppSegmentForRouter(appType),
    currentClient: getClient(appType)
  })),
  on(SharedLoginActions.setBrand, (state, { brand }) => ({
    ...state,
    currentBrand: brand
  })),
  on(SharedLoginActions.login, (state, { email, passwordBase64 }) => ({
    ...state,
    isLoading: true,
    tempEmail: email,
    tempPassword: passwordBase64,
  })),
  on(SharedLoginActions.loginSuccess, (state, { token }) => ({
    ...state,
    isGuest: token?.info?.isGuest ?? false,
    currentToken: token,
    quickAccessProfile: undefined,
    isMultiTenant: (token?.info?.tenants?.length ?? 0) > 1,
    currentBrand: (token?.info?.tenants?.length ?? 0) === 1 ? token?.info?.tenants![0].brand : undefined,
    currentTenant: (token?.info?.tenants?.length ?? 0) === 1 ? token?.info?.tenants![0] : undefined,
    currentUser: (token?.info?.tenants?.length ?? 0) === 1 ? token?.info?.tenants![0].names![0] : undefined,
    isLoading: false,
    tempEmail: undefined,
    tempPassword: undefined,
  })),
  on(SharedLoginActions.loginFail, (state) => ({
    ...state,
    currentToken: undefined,
    isLoading: false
  })),
  on(SharedLoginActions.setTenant, (state, { tenant }) => ({
    ...state,
    currentBrand: tenant?.brand,
    currentTenant: tenant,
    currentUser: (tenant?.names ?? [])[0] ?? undefined,
  })),
  on(SharedLoginActions.loadLoginStorageSuccess, (state, { loadedState }) => ({
    ...(loadedState ?? state)
  })),
  on(SharedLoginActions.syncLoginStorage, (state) => ({
    ...state,
    tempEmail: undefined,
    tempPassword: undefined,
  })),
  on(SharedLoginActions.getTenantSettings, (state) => ({
    ...state,
    isLoading: true
  })),
  on(SharedLoginActions.getTenantSettingsSuccess, (state, { tenantSettings }) => ({
    ...state,
    currentSettings: tenantSettings
  })),
  on(SharedLoginActions.getTenantSettings, (state) => ({
    ...state,
    isLoading: false
  })),
  on(SharedLoginActions.refreshToken, (state) => ({
    ...state,
    isLoading: true
  })),
  on(SharedLoginActions.refreshTokenSuccess, (state, { newToken }) => ({
    ...state,
    isGuest: newToken?.info?.isGuest ?? false,
    currentToken: state.currentApp === EAppType.Link
      ? {...newToken, resourceId: (state.currentToken as QrTokenDto)?.resourceId}
      : newToken,
    isLoading: false
  })),
  on(SharedLoginActions.refreshTokenFail, (state) => ({
    ...state,
    isLoading: false,
  })),
  on(SharedLoginActions.checkAppPermission, (state, {}) => ({
    ...state,
    isLoading: true
  })),
  on(SharedLoginActions.checkAppPermissionSuccess, (state, {}) => ({
    ...state,
    canUseApp: true,
    isLoading: false
  })),
  on(SharedLoginActions.checkAppPermissionFail, (state, {}) => ({
    ...state,
    canUseApp: false,
    isLoading: false
  })),
  on(SharedLoginActions.changePassword, (state, { email, newPasswordBase64 }) => ({
    ...state,
    isLoading: true,
    tempEmail: email,
    tempPassword: newPasswordBase64,
  })),
  on(SharedLoginActions.changePasswordSuccess, (state) => ({
    ...state,
    isLoading: false,
  })),
  on(SharedLoginActions.changePasswordFail, (state) => ({
    ...state,
    isLoading: false,
    tempEmail: undefined,
    tempPassword: undefined
  })),
  on(SharedLoginActions.getQuickAccessProfile, (state) => ({
    ...state,
    isLoading: true
  })),
  on(SharedLoginActions.getQuickAccessProfileSuccess, (state, { profile }) => ({
    ...state,
    isLoading: false,
    quickAccessProfile: profile,
  })),
);

function getAppSegmentForRouter(appType: EAppType): string {
  switch (appType) {
    case EAppType.Tech:
      return 'tech';
    case EAppType.Team:
      return 'team';
    case EAppType.Link:
      return 'link'
    case EAppType.Orga:
      return 'orga';
  }
}

function getClient(appType: EAppType): Clients {
  switch (appType) {
    case EAppType.Tech:
      return Clients.Tech;
    case EAppType.Team:
      return Clients.Team;
    case EAppType.Link:
      return Clients.Link;
    case EAppType.Orga:
      return Clients.Orga;
  }
}

